"use client";

import { useMemo, useState } from "react";
import { KundaliChart as SharedKundaliChart, type ChartLocale } from "@ishvaram/ui/Jyotish";
import { kundaliRegionalLocale, type KundaliRegionalLocale } from "@/i18n/regional-locales";
import { regionalNakshatraName, regionalRashiName } from "@/lib/jyotish-regional-terms";
import { REGIONAL_RESULT_COPY } from "./kundali/regional-result-copy";
import { resolveRashi } from "@/lib/rashi-presentation";
import { nakshatraNameHi } from "@/lib/vedic-names-hi";
import type { PlanetPosition } from "@/lib/api/kundali";
import SouthIndianKundaliChart, {
  type ChartHouse,
  type ChartPlanet,
} from "./SouthIndianKundaliChart";

/** A chart planet, plus the nakshatra the North Indian detail line shows. */
type KundaliChartPlanet = ChartPlanet & Partial<Pick<PlanetPosition, "nakshatra">>;

/**
 * Hindi display names for the shared chart's selected-graha detail, which
 * prints `rashi` and `nakshatra` as given. Display only: the adapter passes no
 * onPlanetSelect, so no caller reads these values back. Unknown values stay.
 */
function withHindiPositions(planets: readonly KundaliChartPlanet[]): KundaliChartPlanet[] {
  return planets.map((planet) => ({
    ...planet,
    rashi: resolveRashi(planet.rashi)?.name_hi ?? planet.rashi,
    nakshatra: nakshatraNameHi(planet.nakshatra) ?? planet.nakshatra,
  }));
}

/** The same display mapping for an in-language regional edition. */
function withRegionalPositions(
  planets: readonly KundaliChartPlanet[],
  locale: KundaliRegionalLocale
): KundaliChartPlanet[] {
  return planets.map((planet) => ({
    ...planet,
    rashi: regionalRashiName(planet.rashi, locale) ?? planet.rashi,
    nakshatra: regionalNakshatraName(planet.nakshatra, locale) ?? planet.nakshatra,
  }));
}

function nativePositions(
  planets: readonly KundaliChartPlanet[],
  locale: ChartLocale
): readonly KundaliChartPlanet[] {
  if (locale === "hi") return withHindiPositions(planets);
  const regional = kundaliRegionalLocale(locale);
  return regional ? withRegionalPositions(planets, regional) : planets;
}

/**
 * App adapter for the shared, keyboard/touch-accessible Jyotish chart with
 * North Indian (Diamond) and South Indian (Square) toggle options.
 */
export default function KundaliChart({
  planets,
  houses = [],
  locale = "en",
  title,
  subtitle,
  unflaggedDignity,
}: {
  planets: readonly KundaliChartPlanet[];
  houses?: readonly ChartHouse[];
  locale?: ChartLocale;
  /** North Indian chart heading; the shared chart defaults it by locale. */
  title?: string;
  subtitle?: string;
  /** The North Indian detail's reading of a graha with no dignity flags (shared chart). */
  unflaggedDignity?: "standard" | "unknown";
}) {
  const [chartStyle, setChartStyle] = useState<"north" | "south">("north");
  const isHi = locale === "hi";
  const regional = kundaliRegionalLocale(locale);
  const northPlanets = useMemo(() => nativePositions(planets, locale), [locale, planets]);
  const styleLabels = regional
    ? REGIONAL_RESULT_COPY[regional].chartStyle
    : isHi
      ? { north: "उत्तर भारतीय (डायमंड)", south: "दक्षिण भारतीय (चौकोर)" }
      : { north: "North Indian (Diamond)", south: "South Indian (Box)" };

  return (
    <div className="w-full flex flex-col items-center">
      {/* Chart Style Toggle */}
      <div className="flex items-center gap-1.5 p-1 bg-surface-warm rounded-xl mb-4 self-center border border-border-subtle">
        <button
          type="button"
          onClick={() => setChartStyle("north")}
          className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
            chartStyle === "north"
              ? "bg-surface-raised text-text-primary shadow-xs"
              : "text-text-secondary hover:text-text-primary"
          }`}
        >
          {styleLabels.north}
        </button>
        <button
          type="button"
          onClick={() => setChartStyle("south")}
          className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
            chartStyle === "south"
              ? "bg-surface-raised text-text-primary shadow-xs"
              : "text-text-secondary hover:text-text-primary"
          }`}
        >
          {styleLabels.south}
        </button>
      </div>

      {chartStyle === "north" ? (
        <SharedKundaliChart
          planets={northPlanets}
          locale={locale}
          title={title}
          subtitle={subtitle}
          unflaggedDignity={unflaggedDignity}
          className="w-full min-w-0"
        />
      ) : (
        <SouthIndianKundaliChart planets={planets} houses={houses} locale={locale} />
      )}
    </div>
  );
}
